<template>
  <div></div>
</template>

<script>
/**
 * マップ上に船舶を表示するコンポーネント
 * @parent O.MapViewGridItem
 * @props map: Object １マップ分のインスタンスデータ
 * @props isReady: Boolean マップ情報が描画されているかのフラグ
 * @props mapEvent: String マップ上操作でのイベント名
 * @props gridMapSetting Object マップID毎にマップに設定されている全体のデータ
 */
import * as turf from "@turf/turf";
import { MapLayer, MapSource } from "@/config/Map.js";
import { createNamespacedHelpers, mapGetters } from "vuex";
import mapboxgl from "mapbox-gl";
import AisService from "@/services/aisService";
import { DateUtil } from "@/utils";
import { VesselMixin } from "@/mixins";

// Store
const { mapActions: mapActionsOfLiveMap, mapGetters: mapGettersOfLiveMap } =
  createNamespacedHelpers("liveMap");

export default {
  name: "O.VesselMapLayer",
  mixins: [VesselMixin],
  data() {
    return {
      initialized: false,
      popupForVessel: new mapboxgl.Popup({
        offset: 15,
        className: "live-map-popup-for-vessel",
        closeButton: false,
        closeOnClick: false,
      }),
      circleIconUrl: require("@/assets/images/vessel/image_circle.png"),
      isPopup: false,
    };
  },
  props: {
    map: Object,
    isReady: { type: Boolean, default: false },
    mapEvent: String,
    mapId: {
      type: String,
      required: false,
      default: "",
    },
    gridMapSetting: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    ...mapGettersOfLiveMap(["isMonitoringLimit", "isMonitoring"]),
    ...mapGetters({
      vesselPositionCollection: "vesselPositionCollection",
    }),
    isFreeAccount() {
      return this.$store.state.isFree === true;
    },
    filterVessels() {
      if (!this.isReady) return [];
      const { gridMapSetting } = this;
      if (!gridMapSetting) return [];
      return gridMapSetting.filter_vessels || [];
    },
  },
  watch: {
    mapEvent() {
      this.removePopupForVessel(this);
    },
    isReady() {
      if (this.isReady) {
        this.isMapReady();
        let filters = ["in", "c"].concat(
          this.filterVessels.map((item) => parseInt(item, 10)),
        );
        this.filterByVesselCategories(filters);
      }
      if (!this.initialized) {
        this.initialized = true;
      }
    },
    filterVessels: {
      handler(val) {
        if (this.isReady) {
          let filters = ["in", "c"].concat(
            val.map((item) => parseInt(item, 10)),
          );
          this.filterByVesselCategories(filters);
        }
      },
      deep: true,
    },
  },
  methods: {
    ...mapActionsOfLiveMap(["getLastPosition", "addMonitoringVessel"]),

    showPopup(e, self) {
      if (this.isPopup) return;
      this.isPopup = true;
      let coordinates = e.features[0].geometry.coordinates;
      // Polygonの場合
      if (e.features[0].geometry.type != "Point") {
        let collections = coordinates[0].map((c) => {
          return turf.point(c);
        });
        let features = turf.featureCollection(collections);
        let center = turf.center(features);

        coordinates = center.geometry.coordinates;
      }

      let vesselId = e.features[0].properties.id;
      let job = [];
      job.push(AisService.getStaticInfo(vesselId));
      job.push(
        AisService.getDynamicAisInfo(vesselId, this.vesselPositionCollection),
      );

      Promise.all(job).then((values) => {
        let aisInfo = {
          s: values[0],
          d: values[1],
        };
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }
        let description = self.crateVesselInfoHtml(aisInfo);
        this.popupForVessel
          .setLngLat(coordinates)
          .setHTML(description)
          .addTo(self.map);
      });
    },

    removePopupForVessel(self) {
      self.popupForVessel.remove();
    },
    crateVesselInfoHtml(aisInfo) {
      let speedLabel = this.$t("Views.Vessel.Velocity");
      let speed = aisInfo.d.v !== undefined ? aisInfo.d.v : "0.0";
      let headingLabel = this.$t("Views.Vessel.Heading");
      let heading =
        aisInfo.d.h !== undefined || aisInfo.d.h === 0
          ? aisInfo.d.h
          : aisInfo.d.d !== undefined
          ? aisInfo.d.d
          : "0";
      let lastReceivedLabel = this.$t("Views.Vessel.LastReceived");
      let lastReceived =
        aisInfo.d.t !== undefined
          ? DateUtil.elapsedTimeByTimestamp(aisInfo.d.t)
          : "---";
      let categoryLabel = this.$t("Views.Vessel.Category");
      let category =
        aisInfo.s.c !== undefined
          ? this.vesselCategory.properties[aisInfo.s.c].name_ja
          : "---";
      let html = `<span class="mr-2">${speedLabel}: <strong>${speed} kn</strong></span><span>${headingLabel}: <strong>${heading}°</strong></span><br><span class="mr-2">${categoryLabel}: <strong>${category}</strong></span><span>${lastReceivedLabel}: <strong>${lastReceived}</strong></span>`;
      return html;
    },
    setCursor(isPointer, self) {
      self.map.getCanvas().style.cursor = isPointer ? "pointer" : "";
    },
    setPointerStyle() {
      let self = this;
      this.map.on("mousemove", MapLayer.VesselSymbolLayer.id, (e) => {
        self.setCursor(true, self);
        self.showPopup(e, self);
      });
      this.map.on("mouseleave", MapLayer.VesselSymbolLayer.id, () => {
        self.setCursor(false, self);
        this.isPopup = false;
        //self.removePopupForVessel(self);
      });
      this.map.on("mousemove", MapLayer.VesselCircleLayer.id, () => {
        self.setCursor(true, self);
      });
      this.map.on("mouseleave", MapLayer.VesselCircleLayer.id, () => {
        self.setCursor(false, self);
      });
      this.map.on("mousemove", MapLayer.VesselPolygonLayer.id, (e) => {
        self.setCursor(true, self);
        self.showPopup(e, self);
      });
      this.map.on("mouseleave", MapLayer.VesselPolygonLayer.id, () => {
        self.setCursor(false, self);
        //self.removePopupForVessel(self);
        this.isPopup = false;
      });
      this.map.on("mousemove", MapLayer.VesselSymbolFarLayer.id, () => {
        self.setCursor(true, self);
      });
      this.map.on("mouseleave", MapLayer.VesselSymbolFarLayer.id, () => {
        self.setCursor(false, self);
      });
      this.map.on("mousemove", MapLayer.VesselSizeWithoutLayer.id, (e) => {
        self.setCursor(true, self);
        self.showPopup(e, self);
      });
      this.map.on("mouseleave", MapLayer.VesselSizeWithoutLayer.id, () => {
        self.setCursor(false, self);
        //self.removePopupForVessel(self);
        this.isPopup = false;
      });
      this.map.on("mousemove", MapLayer.OwnVesselMarkerLayer.id, () => {
        self.setCursor(true, self);
      });
      this.map.on("mouseleave", MapLayer.OwnVesselMarkerLayer.id, () => {
        self.setCursor(false, self);
      });
      this.map.on("mousemove", MapLayer.SelectedVesselMarkerLayer.id, () => {
        self.setCursor(true, self);
      });
      this.map.on("mouseleave", MapLayer.SelectedVesselMarkerLayer.id, () => {
        self.setCursor(false, self);
      });
      this.map.on("mousemove", function () {
        if (!self.isPopup) {
          self.removePopupForVessel(self);
        }
      });
    },
    filterByVesselCategories(vesselCategoryFilters) {
      if (!this.map) return;

      if (this.map.getLayer(MapLayer.VesselSymbolLayer.id)) {
        this.map.setFilter(MapLayer.VesselSymbolLayer.id, [
          "all",
          vesselCategoryFilters,
        ]);
      }
      if (this.map.getLayer(MapLayer.VesselSymbolFarLayer.id)) {
        this.map.setFilter(MapLayer.VesselSymbolFarLayer.id, [
          "all",
          vesselCategoryFilters,
          [">", "v", 0.2],
        ]);
      }
      if (this.map.getLayer(MapLayer.VesselCircleLayer.id)) {
        this.map.setFilter(MapLayer.VesselCircleLayer.id, [
          "all",
          vesselCategoryFilters,
          ["<=", "v", 0.2],
        ]);
      }
      if (this.map.getLayer(MapLayer.VesselTextLayer.id)) {
        this.map.setFilter(MapLayer.VesselTextLayer.id, [
          "all",
          vesselCategoryFilters,
        ]);
      }
      if (this.map.getLayer(MapLayer.VesselPolygonLayer.id)) {
        this.map.setFilter(MapLayer.VesselPolygonLayer.id, [
          "all",
          vesselCategoryFilters,
        ]);
      }
      if (this.map.getLayer(MapLayer.VesselSizeWithoutLayer.id)) {
        this.map.setFilter(MapLayer.VesselSizeWithoutLayer.id, [
          "all",
          vesselCategoryFilters,
          ["==", "is_size", 0],
        ]);
      }
      if (this.map.getLayer(MapLayer.VesselVectorLayer.id)) {
        this.map.setFilter(MapLayer.VesselVectorLayer.id, [
          "all",
          vesselCategoryFilters,
        ]);
      }
    },
    /** レイヤーの描画処理 */
    isMapReady() {
      if (!this.initialized) {
        this.setPointerStyle();
      }
      // VesselVectorLineLayer
      this.map.addLayer({
        id: MapLayer.VesselVectorLayer.id,
        type: "line",
        source: MapSource.VesselVectorSource.id,
        minzoom: 10,
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": "#DA514B",
          "line-width": 2.0,
          "line-opacity": {
            type: "identity",
            property: "o",
          },
        },
      });

      // VesselSymbolLayer
      this.map.addLayer({
        id: MapLayer.VesselSymbolLayer.id,
        type: "symbol",
        source: MapSource.VesselPointSource.id,
        minzoom: 10,
        maxzoom: 14,
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "{i}",
          "icon-rotate": {
            type: "identity",
            property: "r",
          },
        },
        paint: {
          "icon-opacity": {
            type: "identity",
            property: "o",
          },
        },
      });

      // VesselCircleLayer
      this.map.loadImage(this.circleIconUrl, (error, image) => {
        if (error) throw error;
        this.map.addImage("sdf-circle", image, { sdf: true });
      });
      this.map.addLayer({
        id: MapLayer.VesselCircleLayer.id,
        type: "symbol",
        source: MapSource.VesselPointSource.id,
        minzoom: 1,
        maxzoom: 10,
        filter: ["<=", "v", 0.2],
        layout: {
          "icon-allow-overlap": false,
          "icon-ignore-placement": false,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "sdf-circle",
          "icon-size": 0.6,
        },
        paint: {
          "icon-color": {
            type: "identity",
            property: "co",
          },
          "icon-halo-width": 0,
          "icon-opacity": {
            type: "identity",
            property: "o",
          },
        },
      });

      // VesselSymbolFarLayer
      this.map.addLayer({
        id: MapLayer.VesselSymbolFarLayer.id,
        type: "symbol",
        source: MapSource.VesselPointSource.id,
        minzoom: 1,
        maxzoom: 10,
        filter: [">", "v", 0.2],
        layout: {
          "icon-allow-overlap": false,
          "icon-ignore-placement": false,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "{i}",
          "icon-rotate": {
            type: "identity",
            property: "r",
          },
        },
        paint: {
          "icon-opacity": {
            type: "identity",
            property: "o",
          },
        },
      });

      // VesselPolygonLayer
      this.map.addLayer({
        id: MapLayer.VesselPolygonLayer.id,
        type: "fill",
        source: MapSource.VesselPolygonSource.id,
        minzoom: 14,
        maxzoom: 24,
        paint: {
          "fill-color": {
            type: "identity",
            property: "co",
          },
        },
      });

      // VesselSizeWithoutLayer
      this.map.addLayer({
        id: MapLayer.VesselSizeWithoutLayer.id,
        type: "symbol",
        source: MapSource.VesselPointSource.id,
        minzoom: 14,
        filter: ["all", ["==", ["number", ["get", "is_size"]], 0]],
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "{i}",
          "icon-rotate": {
            type: "identity",
            property: "r",
          },
        },
        paint: {
          "icon-opacity": {
            type: "identity",
            property: "o",
          },
        },
      });

      // VesselTextLayer
      this.map.addLayer({
        id: MapLayer.VesselTextLayer.id,
        type: "symbol",
        source: MapSource.VesselPointSource.id,
        minzoom: 10,
        layout: {
          "text-field": "{n}",
          "text-anchor": "top",
          "text-size": 14,
          "text-line-height": 1.2,
          "text-offset": [0, 1.1],
        },
        paint: {
          "text-color": {
            type: "identity",
            property: "tco",
          },
          "text-halo-color": "#000000",
          "text-halo-width": 1,
        },
      });

      /*=========================================================================================
       選択船 レイヤー
      ==========================================================================================*/
      this.map.addLayer({
        id: MapLayer.SelectedVesselPolygonLayer.id,
        type: "fill",
        source: MapSource.SelectedVesselPolygonSource.id,
        minzoom: 14,
        maxzoom: 24,
        paint: {
          "fill-color": {
            type: "identity",
            property: "co",
          },
        },
      });

      this.map.addLayer({
        id: MapLayer.SelectedVesselSymbolLayer.id,
        type: "symbol",
        source: MapSource.SelectedVesselPointSource.id,
        minzoom: 8,
        maxzoom: 14,
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "{i}",
          "icon-rotate": {
            type: "identity",
            property: "r",
          },
        },
        paint: {
          "icon-opacity": {
            type: "identity",
            property: "o",
          },
        },
      });

      this.map.addLayer({
        id: MapLayer.SelectedVesselSizeWithoutLayer.id,
        type: "symbol",
        source: MapSource.SelectedVesselPointSource.id,
        minzoom: 14,
        maxzoom: 24,
        filter: ["all", ["==", ["number", ["get", "is_size"]], 0]],
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "{i}",
          "icon-rotate": {
            type: "identity",
            property: "r",
          },
        },
        paint: {
          "icon-opacity": {
            type: "identity",
            property: "o",
          },
        },
      });

      this.map.addLayer({
        id: MapLayer.SelectedVesselTextLayer.id,
        type: "symbol",
        source: MapSource.SelectedVesselPointSource.id,
        layout: {
          "text-field": "{n}",
          "text-anchor": "top",
          "text-size": 14,
          "text-line-height": 1.2,
          "text-offset": [0, 1.1],
        },
        paint: {
          "text-color": "#EFAC48",
          "text-halo-color": "#000000",
          "text-halo-width": 1,
        },
      });

      this.map.addLayer({
        id: MapLayer.SelectedVesselMarkerLayer.id,
        type: "symbol",
        source: MapSource.SelectedVesselMarkerSource.id,
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-image": "{ism}",
          "icon-offset": [0, -15],
        },
      });

      /*=========================================================================================
        自社船 レイヤー
      ==========================================================================================*/
      this.map.addLayer({
        id: MapLayer.OwnVesselPolygonLayer.id,
        type: "fill",
        source: MapSource.OwnVesselPolygonSource.id,
        minzoom: 14,
        maxzoom: 24,
        paint: {
          "fill-color": {
            type: "identity",
            property: "co",
          },
        },
      });

      this.map.addLayer({
        id: MapLayer.OwnVesselSymbolLayer.id,
        type: "symbol",
        source: MapSource.OwnVesselPointSource.id,
        minzoom: 8,
        maxzoom: 14,
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "{i}",
          "icon-rotate": {
            type: "identity",
            property: "r",
          },
        },
        paint: {
          "icon-opacity": {
            type: "identity",
            property: "o",
          },
        },
      });

      this.map.addLayer({
        id: MapLayer.OwnVesselSizeWithoutLayer.id,
        type: "symbol",
        source: MapSource.OwnVesselPointSource.id,
        minzoom: 14,
        maxzoom: 24,
        filter: ["all", ["==", ["number", ["get", "is_size"]], 0]],
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "{i}",
          "icon-rotate": {
            type: "identity",
            property: "r",
          },
        },
        paint: {
          "icon-opacity": {
            type: "identity",
            property: "o",
          },
        },
      });

      this.map.addLayer({
        id: MapLayer.OwnVesselTextLayer.id,
        type: "symbol",
        source: MapSource.OwnVesselPointSource.id,
        layout: {
          "icon-allow-overlap": true,
          "text-field": "{n}",
          "text-anchor": "top",
          "text-size": 14,
          "text-line-height": 1.2,
          "text-offset": [0, 1.1],
        },
        paint: {
          "text-color": "#54B1FC",
          "text-halo-color": "#000000",
          "text-halo-width": 1,
        },
      });

      this.map.addLayer({
        id: MapLayer.OwnVesselMarkerLayer.id,
        type: "symbol",
        source: MapSource.OwnVesselMarkerSource.id,
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-image": "{iom}",
          "icon-offset": [0, -15],
        },
      });

      /*=========================================================================================
        アンカー船 レイヤー
      ==========================================================================================*/
      // アンカー船のポリゴン
      this.map.addLayer({
        id: MapLayer.AnchorVesselPolygonLayer.id,
        type: "fill",
        source: MapSource.AnchorVesselPolygonSource.id,
        minzoom: 14,
        maxzoom: 24,
        paint: {
          "fill-color": {
            type: "identity",
            property: "co",
          },
        },
      });

      // アンカー船のアイコン
      this.map.addLayer({
        id: MapLayer.AnchorVesselSymbolLayer.id,
        type: "symbol",
        source: MapSource.AnchorVesselPointSource.id,
        minzoom: 8,
        maxzoom: 14,
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "{i}",
          "icon-rotate": {
            type: "identity",
            property: "r",
          },
        },
        paint: {
          "icon-opacity": {
            type: "identity",
            property: "o",
          },
        },
      });

      // アンカー船のSizeWithoutLayer
      this.map.addLayer({
        id: MapLayer.AnchorVesselSizeWithoutLayer.id,
        type: "symbol",
        source: MapSource.AnchorVesselPointSource.id,
        minzoom: 14,
        maxzoom: 24,
        filter: ["all", ["==", ["number", ["get", "is_size"]], 0]],
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "{i}",
          "icon-rotate": {
            type: "identity",
            property: "r",
          },
        },
        paint: {
          "icon-opacity": {
            type: "identity",
            property: "o",
          },
        },
      });

      // アンカー船の吹き出しテキスト
      this.map.addLayer({
        id: MapLayer.AnchorVesselTextLayer.id,
        type: "symbol",
        source: MapSource.AnchorVesselPointSource.id,
        layout: {
          "icon-allow-overlap": true,
          "text-field": "{n}",
          "text-anchor": "top",
          "text-size": 14,
          "text-line-height": 1.2,
          "text-offset": [0, 1.1],
        },
        paint: {
          "text-color": "#54B1FC",
          "text-halo-color": "#000000",
          "text-halo-width": 1,
        },
      });
      // アンカー船のマーカー
      this.map.addLayer({
        id: MapLayer.AnchorVesselMarkerLayer.id,
        type: "symbol",
        source: MapSource.AnchorVesselMarkerSource.id,
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-image": "vessel-anchor-marker",
          "icon-offset": [0, -15],
        },
      });
    },
  },
};
</script>

<style lang="scss"></style>
